import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Spinner from "../../components/Shared/Spinner";
import Input from "../../components/Shared/Input";
import { getTransactions } from "../../api/transactions/GetAllTransactions";
import TransactionsTable from "../../components/Dashboard/transactions/TransactionsTable";

type itemType = "course" | "book" | "package";

const Transactions = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [jumpPage, setJumpPage] = useState(1);
  const [paymobOrderId, setPaymobOrderId] = useState("");
  const [activeTab, setActiveTab] = useState<itemType>("course");

  const [debouncedPaymobOrderId, setDebouncedPaymobOrderId] = useState(paymobOrderId);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPaymobOrderId(paymobOrderId);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [paymobOrderId]);

  // Reset page when filters or tab change
  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedPaymobOrderId]);

  const { data: TransactionsResponse, isLoading: isTransactionsLoading } = useQuery({
    queryFn: () =>
      getTransactions({
        page,
        limit,
        itemType: activeTab,
        paymobOrderId: debouncedPaymobOrderId,
      }),
    queryKey: [
      "transactions",
      page,
      limit,
      activeTab,
      debouncedPaymobOrderId,
    ],
    staleTime: 0,
    refetchInterval: 0,
  });

  const handleNextPage = () => setPage((prev) => prev + 1);
  const handlePreviousPage = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleJumpPage = () => setPage(jumpPage);

  const transactions = TransactionsResponse && "data" in TransactionsResponse ? TransactionsResponse.data.transactions : [];
  const metaData = TransactionsResponse && "data" in TransactionsResponse ? TransactionsResponse.data.metaData : undefined;
  console.log({metaData})

  if (isTransactionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const tabs: { label: string; value: itemType }[] = [
    { label: "Courses", value: "course" },
    { label: "Books", value: "book" },
    { label: "Packages", value: "package" },
  ];

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">Transactions</h1>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="mb-8">
          <h1 className="font-bold text-2xl mb-6">Transaction Details</h1>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === tab.value
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Input
              name="PaymobOrderId"
              type="text"
              className="md:!space-y-0"
              value={paymobOrderId}
              onChange={(e) => setPaymobOrderId(e.target.value)}
              placeholder="Paymob Order ID"
              inputStyles="!rounded !h-[50px]"
            />
          </div>
        </div>

        <TransactionsTable data={transactions} />

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handlePreviousPage}
            disabled={page === 1}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Previous
          </button>
          <span>
            Page {page} of {metaData?.totalPages || 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!metaData || page >= metaData.totalPages}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Next
          </button>
        </div>

        {/* Jump to Page */}
        <div className="flex justify-center items-center mt-4 gap-2">
          <Input
            name="Page"
            type="number"
            value={jumpPage.toString()}
            className="md:!space-y-0"
            inputStyles="!h-10 !rounded !w-24"
            onChange={(e) => setJumpPage(Number(e.target.value))}
          />
          <button
            onClick={handleJumpPage}
            className="bg-blue-500 text-white px-4 h-10 rounded disabled:bg-gray-400"
          >
            Jump to Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default Transactions;